import type { ApolloCache } from '@apollo/client';
import { useMutation } from '@apollo/client/react';

import {
  type NotificationChannel,
  type NotificationSetting,
  type UnusableChannels,
  assertRequiredChannelsStayOn,
} from '@modules/notifications/notificationChannels';
import {
  GET_USER_NOTIFICATION_SETTINGS,
  SAVE_USER_NOTIFICATION_PREFERENCES,
} from '@modules/notifications/notificationQueries';

type SavedPreference = {
  user_id: string;
  type: string;
  email_enabled?: boolean | null;
  sms_enabled?: boolean | null;
  push_enabled?: boolean | null;
  team_leader_cc_enabled?: boolean | null;
};

const writeSavedPreferences = (
  cache: ApolloCache,
  userId: string,
  rows: readonly SavedPreference[],
) => {
  cache.updateQuery(
    { query: GET_USER_NOTIFICATION_SETTINGS, variables: { userId } },
    data =>
      data == null
        ? data
        : {
            ...data,
            notification_settings: data.notification_settings.map(setting => {
              const row = rows.find(saved => saved.type === setting.type);
              return row == null
                ? setting
                : {
                    ...setting,
                    user_preferences: [
                      {
                        __typename: 'user_notification_preferences' as const,
                        email_enabled: row.email_enabled ?? null,
                        sms_enabled: row.sms_enabled ?? null,
                        push_enabled: row.push_enabled ?? null,
                        team_leader_cc_enabled:
                          row.team_leader_cc_enabled ?? null,
                      },
                    ],
                  };
            }),
          },
  );
};

// Saves one channel for one or more notifications. The new values show at
// once and are rolled back if the save fails.
export const useSaveNotificationPreferences = (userId: string) => {
  const [saveNotifications, saveMutation] = useMutation(
    SAVE_USER_NOTIFICATION_PREFERENCES,
  );

  const savePreferences = async (
    settings: readonly NotificationSetting[],
    channel: NotificationChannel,
    value: boolean,
    unusable?: UnusableChannels,
  ) => {
    if (settings.length === 0) {
      return;
    }
    assertRequiredChannelsStayOn(settings, channel, value, unusable);
    const rows: SavedPreference[] = settings.map(setting => {
      const current = setting.user_preferences[0];
      return {
        user_id: userId,
        type: setting.type,
        email_enabled: current?.email_enabled ?? null,
        sms_enabled: current?.sms_enabled ?? null,
        push_enabled: current?.push_enabled ?? null,
        team_leader_cc_enabled: current?.team_leader_cc_enabled ?? null,
        ...channel.toValues(value),
      };
    });

    // Apollo expects __typename on optimistic results; the generated
    // mutation type leaves it out, so the object is built before the call.
    const optimisticResponse = {
      insert_user_notification_preferences: {
        __typename: 'user_notification_preferences_mutation_response' as const,
        returning: rows.map(row => ({
          __typename: 'user_notification_preferences' as const,
          ...row,
        })),
      },
    };

    await saveNotifications({
      variables: {
        objects: settings.map(setting => ({
          user_id: userId,
          type: setting.type,
          ...channel.toValues(value),
        })),
        updateColumns: [channel.column],
      },
      optimisticResponse,
      update: (cache, { data }) => {
        const returning = data?.insert_user_notification_preferences?.returning;
        if (returning != null) {
          writeSavedPreferences(cache, userId, returning);
        }
      },
    });
  };

  return [savePreferences, saveMutation] as const;
};
